class ProductsReelsSlider {
  constructor() {
    this.videoStates = new Map(); // Store video playback positions
    this.loadedVideos = new Set(); // Track which videos have been loaded
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupSlider());
    } else {
      this.setupSlider();
    }
  }

  setupSlider() {
    // Find all product reels sliders on the page
    const sliders = document.querySelectorAll('products-reels custom-slider .swiper');
    
    sliders.forEach(slider => {
      this.initializeSlider(slider);
    });
  }

  initializeSlider(sliderElement) {
    // Get all videos in this slider
    const videos = sliderElement.querySelectorAll('video');
    
    if (videos.length === 0) return;

    // Initialize video states and lazy loading
    videos.forEach((video, index) => {
      this.videoStates.set(video, { currentTime: 0, paused: true });
      
      // Save current time when video is paused
      video.addEventListener('pause', () => {
        this.videoStates.set(video, { 
          currentTime: video.currentTime, 
          paused: true 
        });
      });
      
      // Update state when video is playing
      video.addEventListener('play', () => {
        this.videoStates.set(video, { 
          currentTime: video.currentTime, 
          paused: false 
        });
      });
    });

    // Load videos for active slide and adjacent slides initially
    this.loadAdjacentVideos(sliderElement, videos);

    // Pause all videos initially
    this.pauseAllVideos(videos);

    // Play video in active slide
    this.playActiveVideo(sliderElement, videos);

    // Listen for slide change events with a small delay to ensure Swiper has updated classes
    sliderElement.addEventListener('slideChange', () => {
      setTimeout(() => {
        this.handleSlideChange(sliderElement, videos);
      }, 50);
    });

    // Also listen for Swiper's built-in events if available
    if (sliderElement.swiper) {
      sliderElement.swiper.on('slideChange', () => {
        setTimeout(() => {
          this.handleSlideChange(sliderElement, videos);
        }, 50);
      });
    }

    // Handle intersection observer for autoplay when slider comes into view
    this.setupIntersectionObserver(sliderElement, videos);
  }

  loadAdjacentVideos(sliderElement, videos) {
    // Find slides using Swiper's CSS classes and data attributes
    const activeSlide = sliderElement.querySelector('.swiper-slide-active');
    const prevSlide = sliderElement.querySelector('.swiper-slide-prev');
    const nextSlide = sliderElement.querySelector('.swiper-slide-next');
    
    const slidesToLoad = [activeSlide, prevSlide, nextSlide];
    
    slidesToLoad.forEach(slide => {
      if (slide) {
        const video = slide.querySelector('video');
        if (video) {
          // Only load if video has data-src attribute (not already loaded)
          const source = video.querySelector('source[data-src]');
          if (source) {
            this.loadVideo(video);
          }
        }
      }
    });
  }

  loadVideo(video) {
    if (this.loadedVideos.has(video)) return;
    
    const source = video.querySelector('source[data-src]');
    if (!source) return;
    
    // Move data-src to src to trigger loading
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
    
    // Mark as loaded
    this.loadedVideos.add(video);
    
    // Load the video
    video.load();
  }

  handleSlideChange(sliderElement, videos) {
    // Load videos for new active slide and its adjacent slides
    this.loadAdjacentVideos(sliderElement, videos);
    
    // Pause all videos (but preserve their current time)
    this.pauseAllVideos(videos);
    
    // Play video in active slide
    this.playActiveVideo(sliderElement, videos);
  }

  playActiveVideo(sliderElement, videos) {
    // Use Swiper's CSS classes for reliable slide detection
    const activeSlide = sliderElement.querySelector('.swiper-slide-active');
    
    if (!activeSlide) return;

    const activeVideo = activeSlide.querySelector('video');
    if (!activeVideo) return;

    // Restore video to its previous position
    const videoState = this.videoStates.get(activeVideo);
    if (videoState && videoState.currentTime > 0) {
      activeVideo.currentTime = videoState.currentTime;
    }

    // Ensure video is ready to play
    if (activeVideo.readyState >= 2) {
      activeVideo.play().catch(error => {
        console.log('Video autoplay prevented:', error);
      });
    } else {
      // Wait for video to be ready
      activeVideo.addEventListener('canplay', () => {
        activeVideo.play().catch(error => {
          console.log('Video autoplay prevented:', error);
        });
      }, { once: true });
    }
  }

  pauseAllVideos(videos) {
    videos.forEach(video => {
      video.pause();
      // Don't reset currentTime - preserve playback position
    });
  }

  setupIntersectionObserver(sliderElement, videos) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Slider is visible, play active video
          this.playActiveVideo(sliderElement, videos);
        } else {
          // Slider is not visible, pause all videos
          this.pauseAllVideos(videos);
        }
      });
    }, {
      threshold: 0.5 // Trigger when 50% of slider is visible
    });

    observer.observe(sliderElement);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductsReelsSlider();
});

// Also initialize immediately if DOM is already ready
if (document.readyState !== 'loading') {
  new ProductsReelsSlider();
}
